import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import HebrewDatePicker from '../../HebrewDatePicker';
import 'react-datepicker/dist/react-datepicker.css';
import { addDoc, collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../index";
import { countries, CountryFlag } from "../../SiumShas/newSium/countriesData";

// Helper function to extract leaf nodes from tree structure
const extractLeafNodes = (nodes, parentPath = '') => {
    let leafNodes = [];

    nodes.forEach((node, index) => {
        const currentPath = parentPath ? `${parentPath}.${index + 1}` : `${index + 1}`;

        if (!node.children || node.children.length === 0) {
            // This is a leaf node - can be taken for learning
            // Only store id and taken status
            leafNodes.push({
                id: node.id,
                taken: false
            });
        } else {
            // Has children - recurse
            const childLeaves = extractLeafNodes(node.children, currentPath);
            leafNodes = leafNodes.concat(childLeaves);
        }
    });

    return leafNodes;
};

// Helper to build display tree with parent titles
const buildDisplayTree = (nodes, depth = 0) => {
    let items = [];

    nodes.forEach((node, index) => {
        const hasChildren = node.children && node.children.length > 0;
        items.push({
            ...node,
            depth,
            index: index + 1,
            isLeaf: !hasChildren
        });

        if (hasChildren) {
            const childItems = buildDisplayTree(node.children, depth + 1);
            items = items.concat(childItems);
        }
    });

    return items;
};

// Modal component for selecting book mapping
const SelectMappingModal = ({ isOpen, onClose, mappings, onSelect, isLoading }) => {
    const [searchTerm, setSearchTerm] = useState('');

    if (!isOpen) return null;

    const filteredMappings = mappings.filter(mapping =>
        mapping.bookName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="copy-modal-overlay" onClick={onClose}>
            <div className="copy-modal-content" onClick={e => e.stopPropagation()}>
                <div className="copy-modal-header">
                    <h2>בחר ספר ללימוד</h2>
                    <button className="copy-modal-close-btn" onClick={onClose}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>

                <div className="copy-modal-search">
                    <input
                        type="text"
                        placeholder="חפש לפי שם ספר..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="copy-modal-search-input"
                    />
                </div>

                <div className="copy-modal-body">
                    {isLoading ? (
                        <div className="copy-modal-loading">
                            <div className="updateStatusSpinner" style={{ width: '40px', height: '40px', borderWidth: '4px' }}></div>
                            <span>טוען מיפויים...</span>
                        </div>
                    ) : filteredMappings.length === 0 ? (
                        <div className="copy-modal-empty">
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                            </svg>
                            {searchTerm ? 'לא נמצאו מיפויים תואמים' : 'אין מיפויים קיימים'}
                        </div>
                    ) : (
                        <ul className="copy-mapping-list">
                            {filteredMappings.map(mapping => (
                                <li
                                    key={mapping.id}
                                    className="copy-mapping-list-item"
                                    onClick={() => onSelect(mapping)}
                                >
                                    <div className="copy-mapping-item-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                        </svg>
                                    </div>
                                    <div className="copy-mapping-item-details">
                                        <span className="copy-mapping-item-name">{mapping.bookName}</span>
                                        <span className="copy-mapping-item-date">
                                            {mapping.treeData ? `${extractLeafNodes(mapping.treeData).length} חלקים ללימוד` : ''}
                                        </span>
                                    </div>
                                    <div className="copy-mapping-item-arrow">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <polyline points="15 18 9 12 15 6"></polyline>
                                        </svg>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="copy-modal-footer">
                    <a
                        href="https://wa.me/9720509552993?text=היי,%20אני%20מעוניין%20להוסיף%20ספר%20חדש%20לרשימת%20הספרים"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="whatsapp-contact-link"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span>לא מצאת את הספר? שלח לנו הודעה ב-WhatsApp</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

// Component to display selected book structure preview
const BookStructurePreview = ({ treeData, bookName }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const displayItems = buildDisplayTree(treeData);
    const leafCount = extractLeafNodes(treeData).length;

    const typeLabels = {
        nose: "נושא",
        helek: "חלק",
        kereh: "כרך",
        siman: "סימן",
        saif: "סעיף",
        perek: "פרק",
        daf: "דף",
        mizmor: "מזמור",
        tora: "תורה",
        shaar: "שער",
        masehet: "מסכת",
        sefer: "ספר",
        halahot: "הלכות",
        seder: "סדר",
        parashat: "פרשת",
        mishna: "משנה",
        alia: "עליה"
    };

    return (
        <div className="book-structure-preview">
            <div className="book-structure-header" onClick={() => setIsExpanded(!isExpanded)}>
                <div className="book-structure-info">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#5DC9E0" strokeWidth="2">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                    </svg>
                    <span className="book-structure-name">{bookName} </span>
                    <span className="book-structure-count">{leafCount} חלקים ללימוד</span>

                    <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }}
                    >
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </div>
            </div>

            {isExpanded && (
                <div className="book-structure-tree">
                    {displayItems.map((item, idx) => (
                        <div
                            key={item.id || idx}
                            className={`book-structure-item ${item.isLeaf ? 'leaf-item' : 'parent-item'}`}
                            style={{ paddingRight: `${item.depth * 20 + 10}px` }}
                        >
                            {item.isLeaf ? (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#5DC9E0" strokeWidth="2">
                                    <circle cx="12" cy="12" r="3"></circle>
                                </svg>
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1F2B70" strokeWidth="2">
                                    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path>
                                </svg>
                            )}
                            <span className="item-type"> {typeLabels[item.type] || item.type} </span>
                            <span className="item-name">{item.name}</span>
                            {item.type === 'daf' && item.dafFrom && item.dafTo && (
                                <span className="item-range">({item.dafFrom} - {item.dafTo})</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * NewSiumAher Component
 *
 * This component allows creating a new Sium for any book (other than Shas Gemara).
 *
 * URL Parameters:
 * - mipuiId: (optional) Pre-select a book by passing its mipuiId as a query parameter
 *   Example: /newSiumAher?mipuiId=ABC123
 *
 *   When mipuiId is provided, the corresponding book will be automatically selected
 *   from the mipui collection and displayed to the user.
 */
const NewSiumAher = () => {
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [fieldsErrors, setFieldsErrors] = useState({});
    const [communityName, setCommunityName] = useState('');
    const [managerName, setManagerName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [dedication, setDedication] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState(countries[0]);
    const [phonePrefix, setPhonePrefix] = useState(countries[0].prefix);
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
    const countryDropdownRef = useRef(null);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Book mapping states
    const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
    const [availableMappings, setAvailableMappings] = useState([]);
    const [isLoadingMappings, setIsLoadingMappings] = useState(false);
    const [selectedMapping, setSelectedMapping] = useState(null);

    // Fetch available mappings
    const fetchMappings = async () => {
        setIsLoadingMappings(true);
        try {
            const mipuiCollection = collection(db, "mipui");
            const querySnapshot = await getDocs(mipuiCollection);
            const mappings = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                if (data.treeData && data.treeData.length > 0) {
                    mappings.push({
                        id: doc.id,
                        ...data
                    });
                }
            });
            // Sort mappings alphabetically by book name
            mappings.sort((a, b) => {
                const nameA = (a.bookName || '').toLowerCase();
                const nameB = (b.bookName || '').toLowerCase();
                return nameA.localeCompare(nameB, 'he');
            });
            setAvailableMappings(mappings);
        } catch (error) {
            console.error("Error fetching mappings:", error);
        } finally {
            setIsLoadingMappings(false);
        }
    };

    const handleOpenMappingModal = () => {
        setIsMappingModalOpen(true);
        fetchMappings();
    };

    const handleSelectMapping = (mapping) => {
        setSelectedMapping(mapping);
        setIsMappingModalOpen(false);
    };

    const handleRemoveMapping = () => {
        setSelectedMapping(null);
    };

    const submitNewSium = async (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        if (siumValidation()) {
            try {
                // Extract leaf node IDs from the selected mapping - simplified format
                const learningPieces = selectedMapping
                    ? extractLeafNodes(selectedMapping.treeData)
                    : [];

                // normalize email: trimmed and lowercased version for searching
                const cleanedEmail = email ? String(email).trim().toLowerCase() : '';

                const newSium = {
                    kehilaName: communityName,
                    organizerName: managerName,
                    organizerPhone: phone,
                    organizerEmail: cleanedEmail,
                    nishmat: dedication,
                    created: new Date().getTime(),
                    lastAccessed: new Date().getTime(),
                    countryCode: selectedCountry.code,
                    phonePrefix: phonePrefix,
                    startSium: startDate,
                    endSium: endDate,
                    // Reference to the mapping document
                    mipuiId: selectedMapping?.id || null,
                    // Book name from the mapping
                    bookName: selectedMapping?.bookName || null,
                    // Learning pieces - only {id, taken} for each leaf node
                    learningPieces: learningPieces,
                    totalPieces: learningPieces.length,
                    takenPieces: 0
                };

                const docRef = await addDoc(collection(db, "siumimAher"), newSium);
                sendEmail(newSium, docRef.id);
            } catch (error) {
                console.error("Error creating sium:", error);
                setFieldsErrors({ submit: 'שגיאה ביצירת הסיום, נסה שוב' });
                setIsSubmitting(false);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    function sendEmail(siumObj, siumId) {
        var data = {
            service_id: 'service_ehcnv4y',
            template_id: 'template_ocjfgap',
            user_id: 'S00AQOC3z6jpB3gp_',
            template_params: {
                'to_name': siumObj.organizerName,
                'email_to': siumObj.organizerEmail,
                'kehila_name': siumObj.kehilaName,
                'nishmat': siumObj.nishmat,
                'book_name': siumObj.bookName,
                'sium_link': 'https://siumshas.co.il/niulSiumAher/' + siumId + '/siumStatus',
                'sium_id': siumId
            }
        };

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data)
        };
        fetch('https://api.emailjs.com/api/v1.0/email/send', requestOptions)
            .then(async response => {
                navigate("/niulSiumAher/" + siumId + "/siumStatus");
            })
            .catch(error => {
                console.error('Email error:', error);
                navigate("/niulSiumAher/" + siumId + "/siumStatus");
            });
    }

    function siumValidation() {
        let newErrors = {};

        if (!communityName.trim()) {
            newErrors.communityName = 'נדרש שם הקהילה/הארגון';
        }

        if (!phone.trim()) {
            newErrors.phone = 'נדרש מספר הפלאפון';
        }

        if (!managerName.trim()) {
            newErrors.managerName = 'נדרש שם המנהל';
        }

        if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'נדרש אימייל תקין';
        }

        if (!startDate || !endDate || (startDate.getTime() > endDate.getTime())) {
            newErrors.dates = 'נדרשת בחירת תאריכים תקינה';
        }

        if (!selectedMapping) {
            newErrors.mapping = 'נדרש לבחור ספר ללימוד';
        }

        setFieldsErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handlePhoneChange = (e) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/\D/g, '');
        setPhone(numericValue);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
                setIsCountryDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Load mapping from URL parameter if mipuiId is provided (only on initial mount)
    useEffect(() => {
        const mipuiId = searchParams.get('mipuiId');
        if (mipuiId) {
            const loadMappingFromUrl = async () => {
                try {
                    const mappingRef = doc(db, "mipui", mipuiId);
                    const mappingDoc = await getDoc(mappingRef);

                    if (mappingDoc.exists()) {
                        const data = mappingDoc.data();
                        if (data.treeData && data.treeData.length > 0) {
                            setSelectedMapping({
                                id: mappingDoc.id,
                                ...data
                            });
                        }
                    }
                } catch (error) {
                    console.error("Error loading mapping from URL:", error);
                }
            };

            loadMappingFromUrl();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run on mount

    const handleCountrySelect = (country) => {
        setSelectedCountry(country);
        setPhonePrefix(country.prefix);
        setIsCountryDropdownOpen(false);
    };

    const handlePrefixChange = (e) => {
        const value = e.target.value.replace(/\D/g, '');
        setPhonePrefix(value);
    };

    return (
        <div className="new-sium-container">
            <div className="logo-icon" onClick={() => window.location = "/"}>
                <svg width="135" height="49" viewBox="0 0 135 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M86.3894 46.9941H85.3384L87.4833 41.3423H84.0193V39.4869H84.9845V40.5594H88.7702L86.3894 46.9941Z" fill="#1F2B70"/>
                    <path d="M82.7686 40.5594V44.2057H81.7605V40.5594H82.7686Z" fill="#1F2B70"/>
                    <path d="M74.643 40.5594H77.5172C79.351 40.5594 80.1017 41.353 80.1017 43.5944V46.9941H77.1096V46.2112H79.0936V43.6159C79.0936 41.9 78.6218 41.3423 77.3456 41.3423H76.6056L75.6189 46.9941H74.6001L75.6189 41.3423H74.643V40.5594Z" fill="#1F2B70"/>
                    <path d="M73.0038 40.5594V46.9941H71.9957V40.5594H73.0038Z" fill="#1F2B70"/>
                    <path d="M65.4352 40.5594H70.465V41.3423H69.6499V46.9941H68.6419V41.3423H65.4352V40.5594Z" fill="#1F2B70"/>
                    <path d="M55.5901 40.5594H58.4643C60.2982 40.5594 61.0489 41.353 61.0489 43.5944V46.9941H58.0568V46.2112H60.0408V43.6159C60.0408 41.9 59.5689 41.3423 58.2927 41.3423H57.5527L56.5661 46.9941H55.5472L56.5661 41.3423H55.5901V40.5594Z" fill="#1F2B70"/>
                    <path d="M48.8315 40.5594H51.8987C53.5502 40.5594 54.2259 41.16 54.2259 43.3049V46.9941H53.2178V43.3156C53.2178 41.7284 52.7888 41.3423 51.6306 41.3423H49.8396V46.9941H48.8315V40.5594Z" fill="#1F2B70"/>
                    <path d="M46.7955 40.5594V46.9941H45.7874V40.5594H46.7955Z" fill="#1F2B70"/>
                    <path d="M42.0379 46.9941H40.9869L43.1318 41.3423H39.6678V39.4869H40.633V40.5594H44.4187L42.0379 46.9941Z" fill="#1F2B70"/>
                    <path d="M33.0906 40.5594H38.4635V44.3023C38.4635 46.2863 37.7343 46.9941 36.0612 46.9941H35.3856V46.2112H35.9111C36.9621 46.2112 37.4662 45.8144 37.4662 44.313V41.3423H33.0906V40.5594ZM33.1549 48.6028V43.2298H34.163V48.6028H33.1549Z" fill="#1F2B70"/>
                    <path d="M27.9071 40.5594V46.2112H28.7758V46.9941H23.3707V46.2112H26.899V41.3423H23.3707V40.5594H27.9071Z" fill="#1F2B70"/>
                    <path d="M16.5361 40.5594H21.909V44.3023C21.909 46.2863 21.1798 46.9941 19.5067 46.9941H18.8311V46.2112H19.3566C20.4076 46.2112 20.9116 45.8144 20.9116 44.313V41.3423H16.5361V40.5594ZM16.6004 48.6028V43.2298H17.6085V48.6028H16.6004Z" fill="#1F2B70"/>
                    <path d="M12.9795 46.9941H11.9285L14.0734 41.3423H10.6094V39.4869H11.5746V40.5594H15.3604L12.9795 46.9941Z" fill="#1F2B70"/>
                    <path d="M9.25981 40.5594V46.9941H8.25171V40.5594H9.25981Z" fill="#1F2B70"/>
                    <path d="M0.484871 40.5594H3.93815C5.58971 40.5594 6.35115 41.2351 6.35115 43.3049V46.9941H5.34305V43.3907C5.34305 41.8142 4.82827 41.3423 3.72366 41.3423H2.40455V46.9941H0.120239V46.2112H1.43935V41.3423H0.484871V40.5594Z" fill="#1F2B70"/>
                    <path d="M78.8004 23.4675C78.2379 23.4675 77.5854 23.1975 77.5854 22.365V19.575C77.5854 19.1025 77.9679 18.8325 78.2829 18.8325C78.3054 19.3275 78.7554 19.575 79.2054 19.575H85.7304C87.6879 19.575 88.6104 20.835 88.6104 22.6575V28.98L87.0354 32.6925L79.1154 32.6475C78.6429 31.5675 77.8554 29.565 77.8779 27.36C77.9004 25.6275 78.8904 24.5475 80.1504 23.4675H78.8004ZM79.9254 28.755H88.4529L88.2954 25.92C88.2054 24.255 87.4629 23.4675 85.8429 23.4675H80.3979C79.0029 24.66 79.0479 26.145 79.9254 28.755Z" fill="#1F2B70"/>
                    <path d="M75.749 20.6325C75.9515 21.7575 75.929 24.1425 74.399 27.2925L74.219 27.2025C75.2315 25.0425 75.1865 23.4675 73.769 23.4675H71.519C70.934 23.4675 70.3265 23.1975 70.3265 22.365V19.575C70.3265 19.1025 70.709 18.8325 70.979 18.8325C71.0465 19.35 71.384 19.575 71.9465 19.575H74.2865C75.1865 19.575 75.614 19.9125 75.749 20.6325Z" fill="#1F2B70"/>
                    <path d="M68.2405 24.165C68.2855 23.7825 68.218 23.4675 67.813 23.4675H64.7755C64.213 23.4675 63.5605 23.1975 63.5605 22.365V19.575C63.5605 19.1025 63.988 18.8325 64.258 18.8325C64.2805 19.35 64.708 19.575 65.1805 19.575H67.5205C68.5555 19.575 68.713 19.9125 68.668 21.06L68.2855 25.5825C68.1505 27.0675 69.343 29.0925 69.163 30.9375C69.118 31.7025 68.938 32.7375 67.408 32.7375L68.2405 24.165Z" fill="#1F2B70"/>
                    <path d="M58.985 19.575C60.92 19.575 61.91 20.79 61.91 22.59V28.89L61.595 32.6475H51.5375V27.6075C51.5375 25.875 53.045 24.3 53.765 23.4675H52.1675C51.65 23.4675 50.9975 23.1975 50.9975 22.365V19.575C50.9975 19.1025 51.4025 18.81 51.65 18.8325C51.695 19.35 52.1225 19.575 52.595 19.575H58.985ZM53.2025 28.755H61.7075L61.595 25.8525C61.5275 24.1875 60.83 23.4675 59.21 23.4675H53.99C53.27 24.2325 53.2025 24.5025 53.2025 25.5375V28.755Z" fill="#1F2B70"/>
                    <path d="M35.6514 23.4675C35.0664 23.4675 34.4139 23.1975 34.4139 22.365V19.575C34.4139 19.1025 34.8414 18.8325 35.1339 18.8325C35.1339 19.3725 35.6064 19.575 36.0789 19.575H43.6614C44.5839 19.575 44.7414 20.0025 44.7414 20.7V23.4675H43.3464V25.8525C43.3464 27.36 44.7864 29.0925 44.7864 30.9375C44.7864 31.7025 44.2239 32.715 43.1664 32.7375V23.4675H35.6514ZM34.5039 32.7375V29.5425C34.5039 27.72 35.1339 26.64 35.6964 26.2575C35.9664 26.0775 36.2364 26.0775 36.3714 26.1225C36.1914 26.4825 35.9664 27.2025 35.9664 27.8775C35.9664 28.89 36.2589 29.97 36.2589 31.3875C36.2589 32.2875 35.7639 32.715 34.5039 32.7375Z" fill="#1F2B70"/>
                    <path d="M24.8145 23.4675C24.297 23.4675 23.6445 23.1975 23.6445 22.365V19.575C23.6445 19.1025 24.0045 18.8325 24.297 18.8325C24.342 19.2825 24.792 19.575 25.2645 19.575H26.7945C27.717 19.575 27.897 20.0925 27.897 20.5425V23.4675H26.547L22.632 28.755H27.582C29.877 28.755 30.192 28.17 30.9795 26.8425C32.2845 24.6375 32.5545 24.4575 32.5545 23.4675H29.6745C29.157 23.4675 28.5045 23.1975 28.5045 22.365V19.575C28.5045 19.1025 28.8645 18.8325 29.157 18.8325C29.202 19.2825 29.652 19.575 30.1245 19.575H31.0695C32.217 19.575 32.712 20.0475 32.712 21.015V23.58C32.712 25.605 31.002 25.5375 31.002 30.105C31.002 32.1975 29.922 32.6475 28.0095 32.6475H21.147V30.8925C21.147 29.6325 19.572 27.2025 19.572 25.9425C19.572 24.975 20.0445 23.85 21.147 23.4675H19.9095C19.347 23.4675 18.717 23.1975 18.717 22.365V19.575C18.717 19.1025 19.122 18.8325 19.392 18.8325C19.4145 19.2825 19.8645 19.575 20.337 19.575H21.9345C22.812 19.575 23.037 20.0925 23.037 20.5425V23.4675H21.9345C21.372 23.4675 20.1345 23.8275 20.1345 25.1325C20.1345 26.64 22.137 27.4725 22.4295 28.7325L26.322 23.4675H24.8145Z" fill="#1F2B70"/>
                    <path d="M13.6264 18C14.4139 18 14.6389 18.3375 14.7289 18.8775C14.8864 19.845 14.6614 21.96 12.4339 23.1975L12.2764 23.04C12.8164 21.9825 12.9064 20.1825 12.4789 18.1125C12.8614 18.0225 13.2439 18 13.6264 18ZM14.9089 23.04C15.4489 21.9825 15.5389 20.1825 15.1114 18.1125C15.4939 18.0225 15.8764 18 16.2589 18C17.0464 18 17.2714 18.3375 17.3614 18.8775C17.5189 19.845 17.2939 21.96 15.0664 23.1975L14.9089 23.04Z" fill="#1F2B70"/>
                    <path d="M1.215 23.4675C0.6525 23.4675 0 23.1975 0 22.365V19.575C0 19.1025 0.3825 18.8325 0.6975 18.8325C0.72 19.3275 1.17 19.575 1.62 19.575H8.145C10.1025 19.575 11.025 20.835 11.025 22.6575V28.98L9.45 32.6925L1.53 32.6475C1.0575 31.5675 0.27 29.565 0.2925 27.36C0.315 25.6275 1.305 24.5475 2.565 23.4675H1.215ZM2.34 28.755H10.8675L10.71 25.92C10.62 24.255 9.8775 23.4675 8.2575 23.4675H2.8125C1.4175 24.66 1.4625 26.145 2.34 28.755Z" fill="#1F2B70"/>
                    <path d="M115.193 10.8004C113.895 10.8004 112.258 10.1232 112.258 8.03513V5.60816C112.258 4.42306 113.161 3.74586 113.895 3.74586C114.008 4.87452 115.136 5.60816 116.321 5.60816H120.159C122.472 5.60816 122.924 6.90612 122.924 8.03478V10.8004H119.538L109.719 38.8377H122.134C127.89 38.8377 128.68 37.3704 130.655 34.0408C133.928 28.5104 134.605 28.0589 134.605 25.5758H127.382C126.084 25.5758 124.447 24.8986 124.447 22.8106V15.8129C124.447 14.6278 125.35 13.9506 126.084 13.9506C126.197 15.0793 127.325 15.8129 128.51 15.8129H130.881C133.759 15.8129 135 16.998 135 19.4246V25.858C135 30.937 130.711 30.7677 130.711 42.2237C130.711 47.472 128.003 48.6006 123.206 48.6006H105.995V44.1988C105.995 41.0386 102.044 34.9438 102.044 31.7835C102.044 29.3569 103.229 26.5352 105.995 25.5758H102.891C101.48 25.5758 99.9 24.8986 99.9 22.8106V15.8129C99.9 14.6278 100.916 13.9506 101.593 13.9506C101.649 15.0793 102.778 15.8129 103.963 15.8129H107.97C110.17 15.8129 110.735 17.1109 110.735 18.2395V25.5758H107.97C106.559 25.5758 103.455 26.4788 103.455 29.7519C103.455 33.533 108.478 35.621 109.211 38.7813L118.974 10.8004H115.193Z" fill="#1F2B70"/>
                    <path d="M124.313 1.33474C124.08 1.24031 123.824 1.21549 123.576 1.26344C123.329 1.31138 123.102 1.42993 122.924 1.60397L121.275 3.20821L118.351 0.363263C118.112 0.130666 117.788 0 117.45 0C117.112 0 116.788 0.130666 116.549 0.363263L113.625 3.20821L111.976 1.60397C111.798 1.43051 111.571 1.31239 111.324 1.26453C111.076 1.21668 110.82 1.24125 110.587 1.33514C110.354 1.42902 110.155 1.58801 110.015 1.79199C109.875 1.99598 109.8 2.2358 109.8 2.48115V9.04823C109.801 9.87057 110.137 10.6589 110.735 11.2404C111.332 11.8219 112.142 12.149 112.987 12.15H121.912C122.758 12.149 123.568 11.8219 124.165 11.2404C124.763 10.6589 125.099 9.87057 125.1 9.04823V2.48115C125.1 2.23578 125.025 1.9959 124.885 1.79185C124.745 1.58779 124.546 1.42871 124.313 1.33474Z" fill="#1F2B70"/>
                </svg>
            </div>
            <div className="new-sium-form">
                <div className="form-headers">
                    <h1>יצירת סיום ספר חדש</h1>
                    <h3>מלא את הפרטים וצור סיום ספר קבוצתי</h3>
                </div>
                <form onSubmit={submitNewSium} className="form-container">
                    <div className="input-row">
                        <div className="input-container">
                            <input
                                type="text"
                                value={communityName}
                                onChange={(e) => setCommunityName(e.target.value)}
                                className="input"
                                placeholder="שם הקהילה / הארגון"
                                name="kehilaName"
                            />
                            {fieldsErrors.communityName && <div className="fields-error-message">{fieldsErrors.communityName}</div>}
                        </div>
                        <div className="input-container">
                            <input
                                type="text"
                                value={managerName}
                                onChange={(e) => setManagerName(e.target.value)}
                                className="input"
                                placeholder="שם מנהל הסיום"
                                name="name"
                            />
                            {fieldsErrors.managerName && <div className="fields-error-message">{fieldsErrors.managerName}</div>}
                        </div>
                    </div>

                    <div className="input-row phone-with-country">
                        <div className="input-container phone-input-container">
                            <input
                                type="tel"
                                value={phone}
                                onChange={handlePhoneChange}
                                className="input"
                                placeholder="פלאפון"
                                name="phone"
                            />
                            {fieldsErrors.phone && <div className="fields-error-message">{fieldsErrors.phone}</div>}
                        </div>
                        <div className="input-container country-selector" ref={countryDropdownRef}>
                            <div
                                className="custom-country-dropdown"
                                onClick={() => setIsCountryDropdownOpen(!isCountryDropdownOpen)}
                            >
                                <div className="custom-country-selected">
                                    <CountryFlag countryCode={selectedCountry.code} className="country-flag-icon" />
                                    <span className="country-name-text">{selectedCountry.name}</span>
                                    <span className="dropdown-arrow">{isCountryDropdownOpen ? '▲' : '▼'}</span>
                                </div>
                            </div>
                            {isCountryDropdownOpen && (
                                <div className="custom-country-list">
                                    {countries.map(country => (
                                        <div
                                            key={country.code}
                                            className={`custom-country-item ${selectedCountry.code === country.code ? 'selected' : ''}`}
                                            onClick={() => handleCountrySelect(country)}
                                        >
                                            <CountryFlag countryCode={country.code} className="country-flag-icon" />
                                            <span className="country-name-text">{country.name}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <input
                                className="phone-prefix"
                                type="text"
                                value={phonePrefix}
                                onChange={handlePrefixChange}
                                aria-label="Phone prefix"
                                placeholder="קידומת"
                            />
                        </div>
                    </div>

                    <div className="input-row">
                        <div className="input-container">
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                placeholder="אימייל"
                                name="email"
                            />
                            {fieldsErrors.email && <div className="fields-error-message">{fieldsErrors.email}</div>}
                        </div>
                        <div className="input-container">
                            <input
                                type="text"
                                value={dedication}
                                onChange={(e) => setDedication(e.target.value)}
                                className="input"
                                placeholder="לעילוי נשמת/לרפואת/להצלחת"
                                name="nishmat"
                            />
                        </div>
                    </div>

                    {/* Book Mapping Selection */}
                    <div className="input-container full-width book-selection-container">
                        <label className="halukat-shas-label font-size-20">בחירת ספר ללימוד</label>

                        {!selectedMapping ? (
                            <button
                                type="button"
                                className="btn new-sium-button dark-button select-book-btn"
                                onClick={handleOpenMappingModal}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                </svg>
                                בחר ספר מהרשימה
                            </button>
                        ) : (
                            <div className="selected-book-container">
                                <BookStructurePreview
                                    treeData={selectedMapping.treeData}
                                    bookName={selectedMapping.bookName}
                                />
                                <button
                                    type="button"
                                    className="btn change-book-btn dark-button select-book-btn margin-top-10"
                                    onClick={handleRemoveMapping}
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                    החלף ספר
                                </button>
                            </div>
                        )}
                        {fieldsErrors.mapping && <div className="fields-error-message">{fieldsErrors.mapping}</div>}
                    </div>

                    <div className="input-row">
                        <div className="input-container">
                            <label className="sium-dates-picker">תאריך תחילת הלימוד</label><br/>
                            <HebrewDatePicker
                                selectsStart
                                selected={startDate}
                                onChange={date => setStartDate(date)}
                                startDate={startDate}
                                placeholder="בחר תאריך התחלה"
                            />
                            {fieldsErrors.dates && <div className="fields-error-message">{fieldsErrors.dates}</div>}
                        </div>
                        <div className="input-container">
                            <label className="sium-dates-picker">תאריך סיום הלימוד</label><br/>
                            <HebrewDatePicker
                                selectsEnd
                                selected={endDate}
                                onChange={date => setEndDate(date)}
                                endDate={endDate}
                                startDate={startDate}
                                minDate={startDate}
                                placeholder="בחר תאריך סיום"
                            />
                        </div>
                    </div>

                    {fieldsErrors.submit && (
                        <div className="fields-error-message center-text">{fieldsErrors.submit}</div>
                    )}

                    {!isSubmitting && <div className="input-container full-width">
                        <button type="submit" className="btn new-sium-button dark-button">יצירת הסיום</button>
                    </div>}
                    {isSubmitting && <div className="input-container full-width">
                        <button type="button" className="btn new-sium-button dark-button">
                            <div className="updateStatusSpinner"></div>
                        </button>
                    </div>}
                </form>
            </div>
            <div className="sium-bottom-page">
                <a className="margin-left-10 no-link" href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }}>חזרה לדף הבית</a>
                <span className="margin-left-10">|</span>
                <span> © כל הזכויות שמורות לאתר סיום הש"ס</span>
            </div>

            {/* Book Mapping Selection Modal */}
            <SelectMappingModal
                isOpen={isMappingModalOpen}
                onClose={() => setIsMappingModalOpen(false)}
                mappings={availableMappings}
                onSelect={handleSelectMapping}
                isLoading={isLoadingMappings}
            />
        </div>
    );
};

export default NewSiumAher;
